import React, { useState, useEffect, useCallback, useRef } from 'react'
import { useLocation, useHistory } from 'react-router-dom'
import Grid from '@mui/material/Grid'
import Paper from '@mui/material/Paper'
import Box from '@mui/material/Box'
import TextField from '@mui/material/TextField'
import Button from '@mui/material/Button'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import MenuItem from '@mui/material/MenuItem'
import Divider from '@mui/material/Divider'
import IconButton from '@mui/material/IconButton'
import InputBase from '@mui/material/InputBase'
import SearchIcon from '@mui/icons-material/Search'
import Add from '@mui/icons-material/Add'
import ImageNotSupportedIcon from '@mui/icons-material/ImageNotSupported'
import Title from '../../component/Title'
import ResetFilterButton from '../../component/ResetFilterButton'
import { LIST_FILTER_SEARCH_FIELD_SX, LIST_FILTER_SEARCH_BUTTON_SX, LIST_FILTER_DIVIDER_SX, LIST_FILTER_SELECT_FIELD_SX, ListFilterBar, ListFilterForm } from '../../component/ListFilterLayout'
import ListTableToolbar, { LIST_TABLE_TOOLBAR_BUTTON_SX } from '../../component/ListTableToolbar'
import ListEmptyState from '../../component/ListEmptyState'
import ListTableContainer from '../../component/ListTableContainer'
import API from '../../component/Axios'
import Pagination from '../../component/Pagination'
import EditIcon from '../../component/EditIcon'
import DeleteAlertDialog from '../../component/DeleteAlertDialog'
import ListActionGroup from '../../component/ListActionGroup'
import Snackbars from '../../component/Snackbars'
import Spiner from '../../component/Spiner'
import StatusChip from '../../component/StatusChip'
import { buildListSearch as buildListSearchQuery, createInitialListState, parseNumberParam } from '../../utils/listPageState'
import { renderDateTime } from '../../utils/dateDisplay'

const DEFAULT_SEARCH_SELECT = {
    searchStatus: 99,
    searchPopupStatus: '',
}

const SEARCH_SELECT_PARAMS = {
    searchStatus: {
        param: 'status',
        defaultValue: DEFAULT_SEARCH_SELECT.searchStatus,
        parse: (value, defaultValue) => parseNumberParam(value, defaultValue),
    },
    searchPopupStatus: {
        param: 'popup',
        defaultValue: DEFAULT_SEARCH_SELECT.searchPopupStatus,
    },
}

const APP_REVIEW_STATUS_CHIP = {
    0: { label: '신청', color: 'warning' },
    1: { label: '승인', color: 'success' },
    2: { label: '반려', color: 'error' },
}

const reviewImagePlaceholderSx = {
    width: 46,
    height: 46,
    borderRadius: 1,
    border: '1px solid',
    borderColor: 'divider',
    backgroundColor: 'action.hover',
    color: 'text.disabled',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
}

const formatReviewStar = (star) => {
    const normalizedStar = String(star ?? '').trim()

    return normalizedStar ? `${normalizedStar}점` : '-'
}

function ReviewImageCell({ row }) {
    if (row.image) {
        return (
            <img
                className="listImg"
                src={`${process.env.REACT_APP_AWS_S3_URL}/${row.image}`}
                alt={`${row.app_sports?.title || '운동시설 리뷰'} 이미지`}
            />
        )
    }

    return (
        <Box sx={reviewImagePlaceholderSx} aria-label="리뷰 이미지 없음">
            <ImageNotSupportedIcon sx={{ fontSize: 18 }} />
        </Box>
    )
}

export default function AppReviews() {
    const location = useLocation()
    const history = useHistory()
    const initialListStateRef = useRef(createInitialListState({
        location,
        defaultSearchSelect: DEFAULT_SEARCH_SELECT,
        selectParams: SEARCH_SELECT_PARAMS,
    }))
    const initialListState = initialListStateRef.current
    const [loading, setLoading] = useState(true)
    const [actionLoading, setActionLoading] = useState(false)
    const [totalCount, setTotalCount] = useState(0)
    const [params, setParams] = useState([])
    const [curPage, setCurPage] = useState(initialListState.curPage)
    const offset = 10
    const [openSnackbar, setOpenSnackbar] = useState(initialListState.snackbar.openSnackbar)
    const [severitySnackbar, setSeveritySnackbar] = useState(initialListState.snackbar.severitySnackbar)
    const [messageSnackbar, setMessageSnackbar] = useState(initialListState.snackbar.messageSnackbar)
    const [searchText, setSearchText] = useState(initialListState.searchText)
    const [searchQuery, setSearchQuery] = useState(initialListState.searchQuery)
    const [searchSelect, setSearchSelect] = useState(initialListState.searchSelect)
    const [listErrorMessage, setListErrorMessage] = useState('')
    const deleteInFlightRef = useRef(false)
    const silentListRefreshRef = useRef(false)

    const getData = useCallback(async (showSpinner = true) => {
        if (showSpinner) {
            setLoading(true)
        }
        setListErrorMessage('')

        try {
            const { data } = await API.post('/api/v1/admin/appSportsReview/getList', {
                page: curPage, offset: offset, searchText: searchQuery, searchSelect: searchSelect
            })

            if (data.code === 200) {
                setTotalCount(data.data?.count || 0)
                setParams(data.data?.rows || [])
                setListErrorMessage('')
            } else if (data.code === 403) {
                history.push('/login')
                return
            } else {
                const errorMessage = data.message || 'App 운동시설 리뷰 목록을 불러오지 못했습니다.'

                setTotalCount(0)
                setParams([])
                setListErrorMessage(errorMessage)
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(errorMessage)
            }
        } catch (_error) {
            const errorMessage = 'App 운동시설 리뷰 목록을 불러오지 못했습니다.'

            setTotalCount(0)
            setParams([])
            setListErrorMessage(errorMessage)
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar(errorMessage)
        } finally {
            setLoading(false)
        }
    }, [curPage, history, searchQuery, searchSelect])

    useEffect(() => {
        const showSpinner = !silentListRefreshRef.current
        silentListRefreshRef.current = false
        getData(showSpinner)
    }, [getData])

    useEffect(() => {
        const nextSearch = buildListSearchQuery({
            curPage,
            searchQuery,
            searchSelect,
            selectParams: SEARCH_SELECT_PARAMS,
        })

        if (location.search !== nextSearch) {
            history.replace({
                pathname: location.pathname,
                search: nextSearch,
            })
        }
    }, [curPage, history, location.pathname, location.search, searchQuery, searchSelect])

    const handleClose = () => {
        setOpenSnackbar(false)
    }

    const updateSearchSelect = (e) => {
        if (actionLoading) {
            return
        }

        if (curPage === 1) {
            silentListRefreshRef.current = true
        }

        setCurPage(1)
        setSearchQuery((searchText || '').trim())
        setSearchSelect({
            ...searchSelect,
            [e.target.name]: e.target.value
        })
    }

    const handleSearchSubmit = (e) => {
        e.preventDefault()
        searchButtonClick()
    }

    const resetFilters = () => {
        if (actionLoading) {
            return
        }

        setCurPage(1)
        setSearchText('')
        setSearchQuery('')
        setSearchSelect({
            ...DEFAULT_SEARCH_SELECT,
        })
    }

    // 입력값과 실제 조회값을 분리해 Enter/검색 버튼 시점만 제어한다.
    const searchButtonClick = () => {
        if (actionLoading) {
            return
        }

        const trimmedSearchText = (searchText || '').trim()

        if (curPage === 1 && searchQuery === trimmedSearchText) {
            getData(false)
            return
        }

        setSearchQuery(trimmedSearchText)

        if (curPage !== 1) {
            setCurPage(1)
            return
        }

        if (searchQuery !== trimmedSearchText) {
            return
        }

        getData(false)
    }

    const handleDelete = async (id) => {
        if (deleteInFlightRef.current) {
            return
        }

        deleteInFlightRef.current = true
        setActionLoading(true)

        try {
            const { data } = await API.post('/api/v1/admin/appSportsReview/delete', { sportsReviewId: id })

            if (data.code === 200) {
                setOpenSnackbar(true)
                setSeveritySnackbar('success')
                setMessageSnackbar('앱 리뷰를 삭제했습니다.')

                if (params.length === 1 && curPage > 1) {
                    setCurPage(curPage - 1)
                } else {
                    await getData()
                }
            } else if (data.code === 403) {
                history.push('/login')
            } else {
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(data.message)
            }
        } catch (_error) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar('앱 리뷰를 삭제하지 못했습니다.')
        } finally {
            setActionLoading(false)
            deleteInFlightRef.current = false
        }
    }

    // 검색어와 선택 필터가 기본값에서 벗어났는지 확인해 빈 상태 안내를 구분한다.
    const hasActiveFilters = () => {
        if ((searchQuery || '').trim()) {
            return true
        }

        return Object.entries(DEFAULT_SEARCH_SELECT).some(([key, defaultValue]) => {
            const currentValue = searchSelect?.[key] ?? defaultValue

            return String(currentValue) !== String(defaultValue)
        })
    }

    // 목록 행이 없을 때 오류, 필터 결과 없음, 기본 빈 목록을 명확히 보여준다.
    const renderEmptyState = () => {
        if (listErrorMessage) {
            return (
                <ListEmptyState
                    variant="error"
                    title="App 운동시설 리뷰 목록을 불러오지 못했습니다."
                    description={listErrorMessage}
                    action={(
                        <Button type="button" size="small" variant="outlined" onClick={() => getData(false)} disabled={actionLoading}>
                            다시 조회
                        </Button>
                    )}
                />
            )
        }

        if (hasActiveFilters()) {
            return (
                <ListEmptyState
                    title="조건에 맞는 App 운동시설 리뷰가 없습니다."
                    description="검색어 또는 필터를 초기화한 뒤 다시 확인해보세요."
                    action={<ResetFilterButton label="필터 초기화" onClick={resetFilters} disabled={actionLoading} />}
                />
            )
        }

        return (
            <ListEmptyState
                title="등록된 App 운동시설 리뷰가 없습니다."
                description="App 운동시설 리뷰를 등록하면 이 목록에 표시됩니다."
            />
        )
    }

    return (
        <Grid item xs={12}>
            {loading ? (
                <Spiner/>
            ) : (
                <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
                    {/* 타이틀 */}
                    <Title>App 운동시설 리뷰 리스트</Title>
                    {/* 검색폼 */}
                    <ListFilterBar>
                        <ListFilterForm onSubmit={handleSearchSubmit} trailing={<ResetFilterButton onClick={resetFilters} disabled={actionLoading} />}>
                            <InputBase
                                sx={LIST_FILTER_SEARCH_FIELD_SX}
                                placeholder="닉네임/시설명"
                                type="search"
                                name="searchText"
                                value={searchText}
                                disabled={actionLoading}
                                onChange={(e) => setSearchText(e.target.value)}
                            />
                            <IconButton type="submit" sx={LIST_FILTER_SEARCH_BUTTON_SX} aria-label="search" disabled={actionLoading}><SearchIcon /></IconButton>
                            <Divider sx={LIST_FILTER_DIVIDER_SX} orientation="vertical" />
                            <TextField
                                label="승인상태"
                                select
                                name="searchStatus"
                                value={searchSelect.searchStatus}
                                onChange={updateSearchSelect}
                                size="small"
                                disabled={actionLoading}
                                sx={LIST_FILTER_SELECT_FIELD_SX}
                            >
                                <MenuItem value={99}>전체</MenuItem>
                                <MenuItem value={0}>신청</MenuItem>
                                <MenuItem value={1}>승인</MenuItem>
                                <MenuItem value={2}>반려</MenuItem>
                            </TextField>
                        </ListFilterForm>
                    </ListFilterBar>
                    
                    {/* 테이블 */}
                    <ListTableToolbar
                        right={(
                            <Button
                                href="/new-appSportsReview"
                                variant="contained"
                                color="success"
                                startIcon={<Add />}
                                size="small"
                                disabled={actionLoading}
                                sx={LIST_TABLE_TOOLBAR_BUTTON_SX}
                            >
                                등록
                            </Button>
                        )}
                    />
                    <Box sx={{ opacity: actionLoading ? 0.5 : 1, pointerEvents: actionLoading ? 'none' : 'auto' }}>
                    <ListTableContainer>
                    <Table size="small" sx={{ minWidth: 900 }}>
                        <caption>Total Count: {totalCount}</caption>
                        <TableHead>
                            <TableRow>
                                <TableCell>No</TableCell>
                                <TableCell>이미지</TableCell>
                                <TableCell>닉네임</TableCell>
                                <TableCell>시설명</TableCell>
                                <TableCell>별점</TableCell>
                                <TableCell>내용</TableCell>
                                <TableCell>승인상태</TableCell>
                                <TableCell>등록일</TableCell>
                                <TableCell align="right">Action</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {params.length > 0 ? params.map((row) => (
                                <TableRow key={row.sportsReviewId} hover>
                                    <TableCell>{row.rownum}</TableCell>
                                    <TableCell>
                                        <ReviewImageCell row={row} />
                                    </TableCell>
                                    <TableCell>
                                        <Box sx={{ fontWeight: 700 }}>
                                            {row.user?.nickName || '-'}
                                        </Box>
                                    </TableCell>
                                    <TableCell>{row.app_sports?.title || '-'}</TableCell>
                                    <TableCell>{formatReviewStar(row.star)}</TableCell>
                                    <TableCell className="listTableLimitText">{row.content || '-'}</TableCell>
                                    <TableCell>
                                        <StatusChip status={row.status} config={APP_REVIEW_STATUS_CHIP} fallbackLabel="상태 없음" />
                                    </TableCell>
                                    {/* <TableCell>{row.status === 1 ? <VisibilityIcon/> : <VisibilityOffIcon/>}</TableCell> */}
                                    <TableCell>{renderDateTime(row.regTime)}</TableCell>
                                    <TableCell align="right">
                                        <ListActionGroup>
                                            <EditIcon path={'appReview'} id={row.sportsReviewId} search={location.search} curPage={curPage} searchText={searchQuery} searchSelect={searchSelect} disabled={actionLoading}/>
                                            <DeleteAlertDialog handleDelete={handleDelete} id={row.sportsReviewId} disabled={actionLoading}/>
                                        </ListActionGroup>
                                    </TableCell>
                                </TableRow>
                            )) : (
                                <TableRow>
                                    <TableCell colSpan={9}>
                                        {renderEmptyState()}
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                    </ListTableContainer>
                    </Box>
                    {totalCount > 0 && <Pagination curPage={curPage} offset={offset} totalCount={totalCount} setCurPage={setCurPage} />}
                    {openSnackbar && <Snackbars open={openSnackbar} severity={severitySnackbar} message={messageSnackbar} handleClose={handleClose}/>}
                </Paper>
            )}
        </Grid>
    )
}
